import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  RefreshCw,
  Star,
  Trash2,
} from 'lucide-react';
import {
  PRODUCT_MEDIA_ACCEPTED_TYPES,
  ProductMediaRole,
  type ProductMediaSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorCode } from '@/services/apiClient';
import {
  useRemovePhotoMutation,
  useReorderPhotosMutation,
  useReplacePhotoMutation,
  useUpdatePhotoMutation,
  useUploadPhotoMutation,
} from '../api/catalog.api';

/** Checked here only to answer quickly; the server decides, and its limit may be lower. */
export const PHOTO_MAX_BYTES = 10_000_000;

const ROLES = [ProductMediaRole.FRONT, ProductMediaRole.SIDE, ProductMediaRole.ADDITIONAL];

interface PhotoGalleryProps {
  productId: string;
  /** Null for the product gallery. */
  variantId: string | null;
  /** This gallery's photographs, in gallery order. */
  photos: ProductMediaSummary[];
  canEdit: boolean;
  /** What the photographs show, for screen readers. */
  subject: string;
  onFeedback: (message: string) => void;
}

/** A device whose main pointer is a finger usually has a camera the file picker can open directly. */
function hasTouchCamera(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;
}

/**
 * One gallery: the product's own, or one variant's.
 *
 * Upload from a file, or take a photograph on a phone or tablet (the file input asks the platform
 * for the rear camera); replace, remove, make primary, change the role, and move a photograph
 * earlier or later. No photograph type is required.
 */
export function PhotoGallery({
  productId,
  variantId,
  photos,
  canEdit,
  subject,
  onFeedback,
}: PhotoGalleryProps) {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const uploadInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const replaceInput = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ProductMediaSummary | null>(null);
  const [viewing, setViewing] = useState<ProductMediaSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [touchCamera] = useState(hasTouchCamera);

  const upload = useUploadPhotoMutation(productId);
  const replace = useReplacePhotoMutation(productId);
  const update = useUpdatePhotoMutation(productId);
  const remove = useRemovePhotoMutation(productId);
  const reorder = useReorderPhotosMutation(productId);
  const busy =
    upload.isPending ||
    replace.isPending ||
    update.isPending ||
    remove.isPending ||
    reorder.isPending;

  const explain = (caught: unknown) => {
    const code = apiErrorCode(caught);
    setError(
      code === 'MEDIA_TOO_LARGE'
        ? t('catalog.media.errors.tooLarge')
        : code === 'MEDIA_UNSUPPORTED_TYPE'
          ? t('catalog.media.errors.unsupported')
          : t('catalog.media.errors.failed'),
    );
  };

  /** A file the server would certainly refuse is refused here, in words, before it is sent. */
  const acceptable = (file: File): boolean => {
    if (!(PRODUCT_MEDIA_ACCEPTED_TYPES as readonly string[]).includes(file.type)) {
      setError(t('catalog.media.errors.unsupported'));
      return false;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setError(t('catalog.media.errors.tooLarge'));
      return false;
    }
    return true;
  };

  const takeFile = (event: ChangeEvent<HTMLInputElement>): File | null => {
    const file = event.target.files?.[0] ?? null;
    // Cleared so choosing the same file again still fires a change.
    event.target.value = '';
    setError(null);
    return file && acceptable(file) ? file : null;
  };

  const onUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = takeFile(event);
    if (!file) return;
    upload.mutate(
      {
        file,
        variantId,
        role: photos.length === 0 ? ProductMediaRole.FRONT : ProductMediaRole.ADDITIONAL,
      },
      { onSuccess: () => onFeedback(t('catalog.media.uploaded')), onError: explain },
    );
  };

  const onReplace = (event: ChangeEvent<HTMLInputElement>) => {
    const file = takeFile(event);
    const mediaId = replacing;
    setReplacing(null);
    if (!file || !mediaId) return;
    replace.mutate(
      { mediaId, file },
      { onSuccess: () => onFeedback(t('catalog.media.replaced')), onError: explain },
    );
  };

  const move = (index: number, offset: -1 | 1) => {
    const order = photos.map((photo) => photo.id);
    const target = index + offset;
    if (target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target] as string, order[index] as string];
    reorder.mutate(
      { variantId, mediaIds: order },
      { onSuccess: () => onFeedback(t('catalog.media.reordered')), onError: explain },
    );
  };

  // Earlier is towards the start of the line: left in English, right in Arabic.
  const EarlierIcon = isRtl ? ChevronRight : ChevronLeft;
  const LaterIcon = isRtl ? ChevronLeft : ChevronRight;
  const accept = PRODUCT_MEDIA_ACCEPTED_TYPES.join(',');

  return (
    <div className="space-y-3">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {photos.length === 0 ? (
        <p className="text-muted text-sm">{t('catalog.media.empty')}</p>
      ) : (
        <ul className="flex flex-wrap gap-3">
          {photos.map((photo, index) => (
            <li key={photo.id} className="border-line w-32 rounded-lg border bg-white p-2">
              <button
                type="button"
                onClick={() => setViewing(photo)}
                className="focus-visible:ring-brand-200 block w-full rounded-md focus-visible:ring-4 focus-visible:outline-none"
                aria-label={t('catalog.media.view', { subject })}
              >
                <img
                  src={photo.thumbnailUrl}
                  alt={t(`catalog.media.roles.${photo.role}`)}
                  loading="lazy"
                  decoding="async"
                  className="bg-canvas aspect-square w-full rounded-md object-cover"
                />
              </button>
              <div className="mt-2 flex min-h-6 flex-wrap items-center gap-1">
                {photo.isPrimary ? <Badge tone="brand">{t('catalog.media.primary')}</Badge> : null}
                {!canEdit ? (
                  <span className="text-muted text-xs">
                    {t(`catalog.media.roles.${photo.role}`)}
                  </span>
                ) : null}
              </div>

              {canEdit ? (
                <div className="mt-2 space-y-2">
                  <Select
                    aria-label={t('catalog.media.role')}
                    value={photo.role}
                    disabled={busy}
                    className="px-2 py-1.5 text-xs"
                    options={ROLES.map((role) => ({
                      value: role,
                      label: t(`catalog.media.roles.${role}`),
                    }))}
                    onChange={(event) =>
                      update.mutate(
                        { mediaId: photo.id, role: event.target.value as ProductMediaRole },
                        { onError: explain },
                      )
                    }
                  />
                  <div className="flex flex-wrap gap-1">
                    {!photo.isPrimary ? (
                      <IconAction
                        label={t('catalog.media.setPrimary')}
                        disabled={busy}
                        onClick={() =>
                          update.mutate(
                            { mediaId: photo.id, primary: true },
                            {
                              onSuccess: () => onFeedback(t('catalog.media.primaryChanged')),
                              onError: explain,
                            },
                          )
                        }
                      >
                        <Star className="size-4" aria-hidden />
                      </IconAction>
                    ) : null}
                    <IconAction
                      label={t('catalog.media.moveEarlier')}
                      disabled={busy || index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <EarlierIcon className="size-4" aria-hidden />
                    </IconAction>
                    <IconAction
                      label={t('catalog.media.moveLater')}
                      disabled={busy || index === photos.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <LaterIcon className="size-4" aria-hidden />
                    </IconAction>
                    <IconAction
                      label={t('catalog.media.replace')}
                      disabled={busy}
                      onClick={() => {
                        setReplacing(photo.id);
                        replaceInput.current?.click();
                      }}
                    >
                      <RefreshCw className="size-4" aria-hidden />
                    </IconAction>
                    <IconAction
                      label={t('catalog.media.remove')}
                      disabled={busy}
                      onClick={() => setRemoving(photo)}
                      danger
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </IconAction>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            isLoading={upload.isPending}
            disabled={busy}
            onClick={() => uploadInput.current?.click()}
          >
            <ImagePlus className="size-4" aria-hidden />
            {t('catalog.media.upload')}
          </Button>
          {touchCamera ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => cameraInput.current?.click()}
            >
              <Camera className="size-4" aria-hidden />
              {t('catalog.media.takePhoto')}
            </Button>
          ) : null}
          <input
            ref={uploadInput}
            type="file"
            accept={accept}
            className="hidden"
            onChange={onUpload}
            data-testid="photo-upload-input"
          />
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onUpload}
            data-testid="photo-camera-input"
          />
          <input
            ref={replaceInput}
            type="file"
            accept={accept}
            className="hidden"
            onChange={onReplace}
            data-testid="photo-replace-input"
          />
        </div>
      ) : null}

      <Modal isOpen={viewing !== null} onClose={() => setViewing(null)} title={subject} size="lg">
        {viewing ? (
          <img
            src={viewing.displayUrl}
            alt={t(`catalog.media.roles.${viewing.role}`)}
            className="bg-canvas mx-auto max-h-[70vh] w-auto max-w-full rounded-md object-contain"
          />
        ) : null}
      </Modal>

      <ConfirmDialog
        isOpen={removing !== null}
        onCancel={() => setRemoving(null)}
        isDestructive
        isConfirming={remove.isPending}
        title={t('catalog.media.removeTitle')}
        message={t('catalog.media.removeMessage')}
        confirmLabel={t('catalog.media.remove')}
        onConfirm={() => {
          const target = removing;
          if (!target) return;
          remove.mutate(target.id, {
            onSuccess: () => {
              setRemoving(null);
              onFeedback(t('catalog.media.removed'));
            },
            onError: (caught) => {
              setRemoving(null);
              explain(caught);
            },
          });
        }}
      />
    </div>
  );
}

function IconAction({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={
        'border-line grid size-8 place-items-center rounded-md border bg-white disabled:cursor-not-allowed disabled:opacity-40 ' +
        (danger ? 'text-red-700 hover:bg-red-50' : 'text-ink hover:bg-canvas')
      }
    >
      {children}
    </button>
  );
}
